import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Typography, App, Card, Flex, Tag, Spin, Empty, theme } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { z } from "zod";
import { useQ_PageHome_Organizations } from "@/hooks/Home/useQ_PageHome_Organizations";

const HomeSearchSchema = z.object({
  denied: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_protected/")({
  validateSearch: HomeSearchSchema.parse,
  component: Page_Home,
});

const DENIED_MESSAGES: Record<string, string> = {
  "god-mode": "Access denied",
  org: "You are not a member of this organization",
};

function Page_Home() {
  const { token } = theme.useToken();
  const { userId, userName } = Route.useRouteContext() as { userId: string; userName: string };
  const { denied } = Route.useSearch();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const qOrganizations = useQ_PageHome_Organizations({ userId });

  useEffect(() => {
    if (denied) {
      message.warning(DENIED_MESSAGES[denied] || "Access denied");
      navigate({ to: "/", search: {}, replace: true });
    }
  }, [denied, message, navigate]);

  return (
    <div>
      <Typography.Title level={2}>Dashboard</Typography.Title>
      <Typography.Paragraph type="secondary">
        Welcome{userName ? `, ${userName}` : ""}.
      </Typography.Paragraph>

      <Typography.Title level={4} style={{ marginTop: token.marginLG }}>
        Your Organizations
      </Typography.Title>

      {qOrganizations.query.isLoading ? (
        <Flex justify="center" style={{ padding: token.paddingXL }}>
          <Spin />
        </Flex>
      ) : qOrganizations.organizations.length === 0 ? (
        <Empty description="No organizations yet. Contact your administrator for access." />
      ) : (
        <Flex gap={token.marginMD} wrap="wrap">
          {qOrganizations.organizations.map((membership) => {
            const org = membership.organizations as { id: string; name: string; status: string; created_at: string };
            return (
              <Link
                key={membership.organization_id}
                to={`/${org.id}/settings` as string}
                style={{ textDecoration: "none" }}
              >
                <Card
                  hoverable
                  style={{ width: 280 }}
                  styles={{ body: { padding: token.paddingLG } }}
                >
                  <Flex justify="space-between" align="start">
                    <div>
                      <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
                        {org.name}
                      </Typography.Text>
                      <br />
                      <Tag
                        color={org.status === "active" ? "green" : org.status === "provisioning" ? "blue" : "default"}
                        style={{ marginTop: token.marginXS }}
                      >
                        {org.status}
                      </Tag>
                    </div>
                    <SettingOutlined style={{ fontSize: token.fontSizeXL, color: token.colorTextTertiary }} />
                  </Flex>
                </Card>
              </Link>
            );
          })}
        </Flex>
      )}
    </div>
  );
}
